// @ts-nocheck - Amplify Gen 2 type compatibility issues
/**
 * G13s Tariff Calculator (Dynamic Three-Rate)
 *
 * @description Calculates costs for G13s solar-optimized three-rate tariff
 * @version 2.1.0
 * @created 2025-01-26
 * @author eCompanion2 Energy Tariff Calculator
 *
 * Extracted from tariff-calculator.ts for better modularity
 */

import type { G13sPricing, MonthlyResult, ConsumptionPattern } from './types';
import {
  VAT_RATE,
  HANDLING_FEE,
  SUBSCRIPTION_FEE,
  TRANSITION_FEE,
  POWER_FEE,
  OZE_FEE,
  COGENERATION_FEE,
  isPowerFeeFrozen,
} from './constants';
import { roundToTwoDecimals, distributeConsumption, isWinterSeason } from './helpers';

/**
 * Calculate G13s (Dynamic three-rate) tariff cost
 *
 * Formula:
 * - Solar off-peak hours (summer 9-17, winter 10-15, weekdays only)
 * - Peak hours (mornings + evenings, weekdays only)
 * - Night hours (21:00-07:00 + all weekends/holidays)
 * - Seasonal variation: Winter (Oct-Mar) vs Summer (Apr-Sep)
 * - Energy cost = kWh × energyPrice + qualityCharge
 * - Distribution cost = fixedFee + (kWh × distributionRate by zone)
 * - Total (gross) = (Energy + Distribution) × 1.23
 *
 * @param monthlyConsumption - kWh consumed in month
 * @param pricing - G13s pricing data
 * @param consumptionPattern - Hourly pattern (weekday/weekend)
 * @param daysInMonth - Number of days in month
 * @param year - Year (for holiday detection)
 * @param month - Month (0-indexed, 0=Jan)
 * @returns Monthly calculation result with peak/off-peak split
 */
export function calculateG13s(
  monthlyConsumption: number,
  pricing: G13sPricing,
  consumptionPattern: ConsumptionPattern,
  daysInMonth: number,
  year: number,
  month: number
): MonthlyResult {
  // Input validation - HIGH-1 Security: Validate for NaN, Infinity, and negative values
  if (!Number.isFinite(monthlyConsumption) || monthlyConsumption < 0) {
    throw new Error('Monthly consumption must be a finite non-negative number');
  }
  if (!pricing) {
    throw new Error('Pricing data is required');
  }
  if (!consumptionPattern) {
    throw new Error('Consumption pattern is required');
  }

  // Distribute consumption across hours
  const hourlyConsumption = distributeConsumption(
    monthlyConsumption,
    consumptionPattern,
    daysInMonth,
    year,
    month
  );

  // Determine season
  const isWinter = isWinterSeason(month);

  // Summer off-peak: 09:00-17:00 (solar production hours)
  // Winter off-peak: 10:00-15:00 (reduced solar hours)
  const solarStart = isWinter ? 10 : 9;
  const solarEnd = isWinter ? 15 : 17;

  // Summer peak: 07:00-09:00 + 17:00-21:00
  // Winter peak: 07:00-10:00 + 15:00-21:00

  // Split into peak and off-peak consumption
  let peakKwh = 0;
  let offPeakKwh = 0;

  for (const hourData of hourlyConsumption) {
    const isWeekendOrHoliday = hourData.isWeekend || hourData.isHoliday;
    const hour = hourData.hour;

    if (isWeekendOrHoliday) {
      // Weekends and holidays: all hours are off-peak (night rate)
      offPeakKwh += hourData.consumption;
    } else {
      // Weekdays: categorize by hour
      const isSolarOffPeak = hour >= solarStart && hour < solarEnd; // Solar production hours
      const isNightOffPeak = hour >= 21 || hour < 7; // Night hours (21:00-07:00)

      if (isSolarOffPeak || isNightOffPeak) {
        offPeakKwh += hourData.consumption;
      } else {
        // Morning and evening peaks
        peakKwh += hourData.consumption;
      }
    }
  }

  // Calculate energy production cost (same rate for all zones)
  const energyPriceTotal = pricing.energyPricePerKwh + (pricing.qualityCharge || 0);
  const productionCost = monthlyConsumption * energyPriceTotal;

  // Calculate distribution cost
  const distributionPeakCost = peakKwh * pricing.distributionPeakPerKwh;
  const distributionOffPeakCost = offPeakKwh * pricing.distributionOffPeakPerKwh;
  const distributionCost = pricing.fixedMonthlyFee + distributionPeakCost + distributionOffPeakCost;

  // Get fees - use defaults if not provided
  const handlingFee = pricing.handlingFee ?? HANDLING_FEE.REGULATED;
  const subscriptionFee = pricing.subscriptionFee ?? SUBSCRIPTION_FEE;
  const transitionFee = pricing.transitionFee ?? TRANSITION_FEE.TIER_3;

  // Power fee is 0 for Jan-June 2025
  const basePowerFee = pricing.powerFee ?? POWER_FEE.TIER_3;
  const powerFee = isPowerFeeFrozen(month, year) ? 0 : basePowerFee;

  // Variable fees (per kWh)
  const ozeFeeAmount = monthlyConsumption * (pricing.ozeFee ?? OZE_FEE);
  const cogenerationFeeAmount = monthlyConsumption * (pricing.cogenerationFee ?? COGENERATION_FEE);

  // Calculate totals
  const subtotal = productionCost + distributionCost +
    handlingFee + subscriptionFee + transitionFee + powerFee +
    ozeFeeAmount + cogenerationFeeAmount;
  const totalCost = subtotal * (1 + VAT_RATE);

  return {
    totalKwh: roundToTwoDecimals(monthlyConsumption),
    peakKwh: roundToTwoDecimals(peakKwh),
    offPeakKwh: roundToTwoDecimals(offPeakKwh),
    productionCost: roundToTwoDecimals(productionCost),
    distributionCost: roundToTwoDecimals(distributionCost),
    handlingFee: roundToTwoDecimals(handlingFee),
    subscriptionFee: roundToTwoDecimals(subscriptionFee),
    transitionFee: roundToTwoDecimals(transitionFee),
    powerFee: roundToTwoDecimals(powerFee),
    ozeFee: roundToTwoDecimals(ozeFeeAmount),
    cogenerationFee: roundToTwoDecimals(cogenerationFeeAmount),
    subtotal: roundToTwoDecimals(subtotal),
    totalCost: roundToTwoDecimals(totalCost)
  };
}
